import React, { useState, useRef } from 'react';
import { Col, Form, Row, Space, Input, message, Collapse } from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  EditOutlined
} from '@ant-design/icons';

const { Panel } = Collapse;
import FormItem from '@/common/FormItem';
import styles from '@/common/Layout/index.module.less';
import CustomModal from '@/common/CustomModal';
import CustomButton from '@/common/CustomButton';
import { getLoggedInUser } from 'utilities/helpers';
import { useDispatch } from 'react-redux';
import { createEvents, reviewEvents } from '../../redux/slice';

const { TextArea } = Input;

const Events = ({
  isEventModel,
  setIsEventModel,
  formDetails,
  handleGetAllBoms
}) => {
  const [form] = Form.useForm();
  const [reviewForm] = Form.useForm();
  const [editingField, setEditingField] = useState(null);
  const [activeKey, setActiveKey] = useState([0]);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(null);
  const [type, SetType] = useState('');
  const addDeviationRef = useRef(null);

  const user = getLoggedInUser();
  const dispatch = useDispatch();

  const handleClose = () => {
    form.resetFields();
    setEditingField(null);
    setActiveKey([0]);
    if (setIsEventModel) setIsEventModel(false);
  };

  const isQAUser = user?.user?.role === 'qc_initiator';
  const isProdEBMRLevel3 =
    user?.user?.role === 'prod_ebmr' && user?.user?.level === 3;

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      console.log('Form data:', values);

      if (isProdEBMRLevel3 && editingField !== null) {
        const editedDeviation = values?.deviations?.[editingField];
        const id =
          editedDeviation?.id || formDetails?.eventsData?.[editingField]?.id;

        if (!id) {
          message.error('Unable to find deviation ID for update');
          return;
        }

        const updatePayload = {
          id,
          step_no: editedDeviation.step_no,
          deviation_no: editedDeviation.deviation_no,
          event_date: editedDeviation.event_date,
          description: editedDeviation.description,
          signDate: editedDeviation.signDate
        };

        dispatch(createEvents(updatePayload)).then((res) => {
          if (res?.payload?.success) {
            message.success(
              res?.payload?.message || 'Deviation updated successfully'
            );
            setEditingField(null);
            handleGetAllBoms();
            handleClose();
          }
        });
      } else {
        const newDeviations =
          values?.deviations?.filter((dev) => !dev?.id) || [];

        if (newDeviations.length === 0) {
          message.warning('No new deviations to submit');
          return;
        }

        const payload = {
          product_id: formDetails?.product_id,
          process_group_id: formDetails?.process_group_id,
          process_id: formDetails?.process_id,
          inst_config_id: formDetails?.inst_config_id,
          dispensing_request_id: formDetails?.dispensing_request_id,
          events: newDeviations
        };

        dispatch(createEvents(payload)).then((res) => {
          if (res?.payload?.success) {
            message.success(res?.payload?.message);
            handleGetAllBoms();
            handleClose();
          }
        });
      }
    });
  };

  const openReviewModal = (fieldIndex, type) => {
    setCurrentReviewIndex(fieldIndex);
    setIsReviewModalOpen(true);
    SetType(type);
    // Pre-fill form if data exists
    const deviation = formDetails?.eventsData?.[fieldIndex];
    if (deviation?.qa_comments) {
      reviewForm.setFieldsValue({
        qa_comments: deviation.qa_comments,
        qa_signDate: `${user?.user?.employee} & ${new Date()
          .toISOString()
          .slice(0, 16)
          .replace('T', ' ')}`
      });
    }
  };

  const handleReviewSubmit = () => {
    reviewForm.validateFields().then((values) => {
      const formValues = form.getFieldsValue();
      const deviation = formValues?.deviations?.[currentReviewIndex];
      const id =
        deviation?.id || formDetails?.eventsData?.[currentReviewIndex]?.id;

      if (!id) {
        message.error('Unable to find deviation ID');
        return;
      }

      const payload =
        isQAUser && type == 'hold'
          ? {
              id,
              type: 'qa_onhold',
              comments: values.comments
            }
          : isQAUser && type == 'Approve'
          ? {
              id,
              type: 'Approved',
              comments: values.comments
            }
          : {
              id,
              // status: 'Qa_reviewed',
              type: 'prod_reviewed',
              comments: values.comments
            };

      dispatch(reviewEvents(payload)).then((res) => {
        if (res?.payload?.success) {
          const updated = [...formDetails.eventsData];
          updated[currentReviewIndex].status = isProdEBMRLevel3
            ? 'Reviewed'
            : isQAUser && type == 'hold'
            ? 'On Hold'
            : 'QA Approved';
          updated[currentReviewIndex].qa_comments = values.qa_comments;
          formDetails.eventsData = updated;
          message.success(res?.payload?.message);
          handleGetAllBoms();
          handleCloseReviewModal();
        }
      });
    });
  };

  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false);
    setCurrentReviewIndex(null);
    reviewForm.resetFields();
  };

  const toggleEdit = (fieldIndex) => {
    if (editingField === fieldIndex) {
      setEditingField(null);
    } else {
      setEditingField(fieldIndex);
    }
  };

  const handleAddDeviation = () => {
    if (addDeviationRef.current) {
      addDeviationRef.current();
    }
  };

  return (
    <>
      <CustomModal
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <span>
              {isQAUser
                ? 'Review Process/Procedure Deviation'
                : 'Process/Procedure Deviation'}
            </span>
            {!isQAUser && !isProdEBMRLevel3 && (
              <CustomButton
                type="primary"
                onClick={handleAddDeviation}
                icon={<PlusOutlined />}
                size="small"
              >
                Add Deviation
              </CustomButton>
            )}
          </div>
        }
        visible={isEventModel}
        width="90%"
        footer={null}
        className={styles.customModal}
        closable={false}
      >
        <br />

        <Form form={form} layout="vertical">
          <Form.List
            name="deviations"
            initialValue={
              formDetails?.eventsData?.length > 0
                ? formDetails?.eventsData.map((item) => ({
                    ...item,
                    event_date: item.event_date
                      ? item.event_date
                          .slice(0, 10)
                          .split('-')
                          .reverse()
                          .join('-')
                      : new Intl.DateTimeFormat('en-GB')
                          .format(new Date())
                          .replace(/\//g, '-')
                  }))
                : [{}]
            }
          >
            {(fields, { add, remove }) => {
              addDeviationRef.current = add;

              return (
                <>
                  <Collapse
                    activeKey={activeKey}
                    onChange={setActiveKey}
                    style={{ marginBottom: 16 }}
                  >
                    {fields.map((field) => {
                      const isEditing = editingField === field.name;
                      const deviationData =
                        formDetails?.eventsData?.[field?.name];
                      console.log(deviationData, 'deviationData');
                      const isApproved =
                        deviationData?.status === 'QA Approved';
                      const isOnhold = deviationData?.status === 'OnHold';
                      const isReviewed = deviationData?.status === 'Reviewed';
                      const isSubmitted = deviationData?.id ? true : false;

                      return (
                        <Panel
                          header={
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%'
                                // minHeight: '0px', // Add this to control header height
                                // padding: '8px 0 !important'
                              }}
                            >
                              <span>
                                <strong>Deviation</strong>
                                {deviationData?.deviation_no &&
                                  ` #${deviationData.deviation_no}`}
                                {isApproved && (
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      color: '#52c41a',
                                      fontSize: '12px'
                                    }}
                                  >
                                    ✓ Approved
                                  </span>
                                )}
                                {isOnhold && (
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      color: '#ea9439ff',
                                      fontSize: '12px'
                                    }}
                                  >
                                    ✓ Onhold
                                  </span>
                                )}
                                {isReviewed && (
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      color: '#1082edff',
                                      fontSize: '12px'
                                    }}
                                  >
                                    ✓ Reviewed
                                  </span>
                                )}
                              </span>
                            </div>
                          }
                          key={field.key}
                          extra={
                            !isQAUser && fields.length > 1 && !isSubmitted ? (
                              <MinusCircleOutlined
                                style={{
                                  fontSize: 18,
                                  color: '#ff4d4f',
                                  cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  remove(field.name);
                                }}
                              />
                            ) : isProdEBMRLevel3 && !isApproved ? (
                              <Space>
                                {!isReviewed && !isApproved && !isOnhold && (
                                  <CustomButton
                                    size="small"
                                    type="primary"
                                    ghost
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openReviewModal(field.name);
                                    }}
                                    disabled={isReviewed || isApproved}
                                  >
                                    Review
                                  </CustomButton>
                                )}
                                <EditOutlined
                                  style={{
                                    fontSize: 18,
                                    color: isEditing ? '#1890ff' : '#8c8c8c',
                                    cursor: 'pointer'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleEdit(field.name);
                                  }}
                                />
                              </Space>
                            ) : (
                              isQAUser &&
                              (isReviewed || isOnhold) && (
                                // <Col span={24}>
                                //   <FormItem className="text-center">
                                <Space>
                                  <CustomButton
                                    className={styles.inwardButton}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openReviewModal(field.name, 'hold');
                                    }}
                                    type="primary"
                                    size="small"
                                    ghost
                                    disabled={isApproved}
                                  >
                                    {'On Hold'}
                                  </CustomButton>
                                  <CustomButton
                                    className={styles.inwardButton}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openReviewModal(field.name, 'Approve');
                                    }}
                                    type="primary"
                                    size="small"
                                    disabled={isApproved}
                                  >
                                    {isApproved ? 'Approved' : 'Approve'}
                                  </CustomButton>
                                </Space>
                                // </FormItem>
                                // </Col>
                              )
                            )
                          }
                        >
                          <div style={{ padding: '16px 0' }}>
                            <Row gutter={24}>
                              <Col span={6}>
                                <FormItem
                                  {...field}
                                  label="Step No."
                                  name={[field.name, 'step_no']}
                                  initialValue={formDetails?.step}
                                  className={styles.customLable}
                                >
                                  <Input
                                    placeholder="Enter Step Number"
                                    disabled
                                  />
                                </FormItem>
                              </Col>
                              <Col span={6}>
                                <FormItem
                                  {...field}
                                  label="Deviation No."
                                  name={[field.name, 'deviation_no']}
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Enter Deviation Number'
                                    }
                                  ]}
                                  className={styles.customLable}
                                >
                                  <Input
                                    placeholder="Enter Deviation Number"
                                    disabled={
                                      isProdEBMRLevel3
                                        ? !isEditing
                                        : isSubmitted
                                    }
                                  />
                                </FormItem>
                              </Col>

                              <Col span={6}>
                                <FormItem
                                  {...field}
                                  label="Date"
                                  name={[field.name, 'event_date']}
                                  initialValue={new Intl.DateTimeFormat(
                                    'en-GB',
                                    {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit'
                                    }
                                  )
                                    .format(new Date())
                                    .replace(/\//g, '-')
                                    .replace(',', '')}
                                  className={styles.customLable}
                                >
                                  <Input disabled />
                                </FormItem>
                              </Col>
                              <Col span={6}>
                                <FormItem
                                  {...field}
                                  label="Sign & Date (Prod)"
                                  name={[field.name, 'signDate']}
                                  className={styles.customLable}
                                  initialValue={`${
                                    user?.user?.employee
                                  } & ${new Intl.DateTimeFormat('en-GB', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    hour12: false
                                  })
                                    .format(new Date())
                                    .replace(/\//g, '-')
                                    .replace(',', '')}`}
                                >
                                  <Input disabled />
                                </FormItem>
                              </Col>
                              <Col span={24}>
                                <FormItem
                                  {...field}
                                  label="Brief Description of the Deviation"
                                  rules={[
                                    {
                                      required: true,
                                      message:
                                        'Enter Brief Description of the Deviation'
                                    }
                                  ]}
                                  name={[field.name, 'description']}
                                  className={styles.customLable}
                                >
                                  <TextArea
                                    placeholder="Description"
                                    rows={3}
                                    disabled={
                                      isProdEBMRLevel3
                                        ? !isEditing
                                        : isSubmitted
                                    }
                                  />
                                </FormItem>
                              </Col>
                              {isProdEBMRLevel3 && (isApproved || isOnhold) && (
                                <>
                                  <Col span={6}>
                                    <FormItem
                                      {...field}
                                      label="Sign & Date (QA)"
                                      name={[field.name, 'qa_sign_date']}
                                      className={styles.customLable}
                                      initialValue={`${
                                        user?.user?.employee
                                      } & ${new Intl.DateTimeFormat('en-GB', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        hour12: false
                                      })
                                        .format(new Date())
                                        .replace(/\//g, '-')
                                        .replace(',', '')}`}
                                    >
                                      <Input disabled />
                                    </FormItem>
                                  </Col>
                                  <Col span={24}>
                                    <FormItem
                                      {...field}
                                      label="QA Comments"
                                      rules={[
                                        {
                                          required: false,
                                          message:
                                            'Enter Brief Description of the Deviation'
                                        }
                                      ]}
                                      name={[field.name, 'qa_comments']}
                                      className={styles.customLable}
                                    >
                                      <TextArea
                                        placeholder="Description"
                                        rows={3}
                                        disabled
                                      />
                                    </FormItem>
                                  </Col>
                                </>
                              )}
                              {!isProdEBMRLevel3 &&
                                !isQAUser &&
                                (isApproved || isOnhold || isReviewed) && (
                                  <>
                                    <Col span={12}>
                                      <FormItem
                                        {...field}
                                        label="Production EBMR Comments"
                                        rules={[
                                          {
                                            required: false,
                                            message:
                                              'Enter Brief Description of the Deviation'
                                          }
                                        ]}
                                        name={[field.name, 'prod_comments']}
                                        className={styles.customLable}
                                      >
                                        <TextArea
                                          placeholder="Description"
                                          rows={3}
                                          disabled
                                        />
                                      </FormItem>
                                    </Col>
                                    <Col span={12}>
                                      <FormItem
                                        {...field}
                                        label="QA Comments"
                                        rules={[
                                          {
                                            required: false,
                                            message:
                                              'Enter Brief Description of the Deviation'
                                          }
                                        ]}
                                        name={[field.name, 'qa_comments']}
                                        className={styles.customLable}
                                      >
                                        <TextArea
                                          placeholder="Description"
                                          rows={3}
                                          disabled
                                        />
                                      </FormItem>
                                    </Col>
                                    <Col span={12}>
                                      <FormItem
                                        {...field}
                                        label="Sign & Date (Prod)"
                                        name={[field.name, 'prod_sign_date']}
                                        className={styles.customLable}
                                        initialValue={`${
                                          user?.user?.employee
                                        } & ${new Intl.DateTimeFormat('en-GB', {
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          second: '2-digit',
                                          hour12: false
                                        })
                                          .format(new Date())
                                          .replace(/\//g, '-')
                                          .replace(',', '')}`}
                                      >
                                        <Input disabled />
                                      </FormItem>
                                    </Col>
                                    <Col span={12}>
                                      <FormItem
                                        {...field}
                                        label="Sign & Date (QA)"
                                        name={[field.name, 'qa_sign_date']}
                                        className={styles.customLable}
                                        initialValue={`${
                                          user?.user?.employee
                                        } & ${new Intl.DateTimeFormat('en-GB', {
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          second: '2-digit',
                                          hour12: false
                                        })
                                          .format(new Date())
                                          .replace(/\//g, '-')
                                          .replace(',', '')}`}
                                      >
                                        <Input disabled />
                                      </FormItem>
                                    </Col>
                                  </>
                                )}
                              {isQAUser && (
                                <>
                                  <Col span={6}>
                                    <FormItem
                                      {...field}
                                      label="Sign & Date (Prod)"
                                      name={[field.name, 'prod_sign_date']}
                                      className={styles.customLable}
                                      initialValue={`${
                                        user?.user?.employee
                                      } & ${new Intl.DateTimeFormat('en-GB', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        hour12: false
                                      })
                                        .format(new Date())
                                        .replace(/\//g, '-')
                                        .replace(',', '')}`}
                                    >
                                      <Input disabled />
                                    </FormItem>
                                  </Col>
                                  <Col span={24}>
                                    <FormItem
                                      {...field}
                                      label="Production EBMR Comments"
                                      rules={[
                                        {
                                          required: true,
                                          message:
                                            'Enter Brief Description of the Deviation'
                                        }
                                      ]}
                                      name={[field.name, 'prod_comments']}
                                      className={styles.customLable}
                                    >
                                      <TextArea
                                        placeholder="Description"
                                        rows={3}
                                        disabled
                                      />
                                    </FormItem>
                                  </Col>
                                </>
                              )}
                            </Row>
                            {isProdEBMRLevel3 && isEditing && (
                              <FormItem className="text-center">
                                <CustomButton
                                  className={styles.inwardButton}
                                  onClick={handleSubmit}
                                  type="primary"
                                  size="small"
                                >
                                  Update
                                </CustomButton>
                              </FormItem>
                            )}
                          </div>
                        </Panel>
                      );
                    })}
                  </Collapse>
                </>
              );
            }}
          </Form.List>

          <FormItem className="text-center">
            <Space>
              <CustomButton
                className={styles.inwardButton}
                onClick={handleClose}
                type="primary"
                size="small"
                ghost
              >
                Close
              </CustomButton>
              {!isQAUser && !isProdEBMRLevel3 && (
                <CustomButton
                  className={styles.inwardButton}
                  onClick={handleSubmit}
                  type="primary"
                  size="small"
                >
                  Submit
                </CustomButton>
              )}
            </Space>
          </FormItem>
        </Form>
      </CustomModal>

      {/* Review Modal */}
      <CustomModal
        title="Review Deviation"
        visible={isReviewModalOpen}
        width="600px"
        footer={null}
        className={styles.customModal}
        closable={false}
      >
        <Form form={reviewForm} layout="vertical">
          <Row gutter={24}>
            <Col span={24}>
              <FormItem
                label="Review Comments"
                name="comments"
                rules={[
                  {
                    required: true,
                    message: 'Please enter review comments'
                  }
                ]}
                className={styles.customLable}
              >
                <TextArea placeholder="Enter your review comments" rows={4} />
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem
                label="Sign & Date"
                name="signDate"
                className={styles.customLable}
                initialValue={`${
                  user?.user?.employee
                } & ${new Intl.DateTimeFormat('en-GB', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: false
                })
                  .format(new Date())
                  .replace(/\//g, '-')
                  .replace(',', '')}`}
              >
                <Input disabled />
              </FormItem>
            </Col>
          </Row>

          <FormItem className="text-center">
            <Space>
              <CustomButton
                className={styles.inwardButton}
                onClick={handleCloseReviewModal}
                type="primary"
                size="small"
                ghost
              >
                Cancel
              </CustomButton>
              <CustomButton
                className={styles.inwardButton}
                onClick={handleReviewSubmit}
                type="primary"
                size="small"
              >
                {type === 'hold' ? 'On hold' : 'Review'}
              </CustomButton>
            </Space>
          </FormItem>
        </Form>
      </CustomModal>
    </>
  );
};

export default Events;
